// src/api/authSession.js
/**
 * Centralized Authentication & Session Management
 * Handles:
 * - Session-only persistence (auto-cleared when browser/tab is closed)
 * - 30-minute inactivity auto-logout
 * - Safe session clearing & redirection
 */

const TOKEN_KEY = "adminToken";
const ACTIVITY_KEY = "lastActivityTimestamp";
export const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Get active token from sessionStorage (or migrate legacy localStorage)
 */
export const getAuthToken = () => {
  let token = sessionStorage.getItem(TOKEN_KEY);
  if (!token) {
    // Migration check: If stored in localStorage from previous version, move to sessionStorage
    const legacyToken = localStorage.getItem(TOKEN_KEY);
    if (legacyToken) {
      sessionStorage.setItem(TOKEN_KEY, legacyToken);
      localStorage.removeItem(TOKEN_KEY);
      token = legacyToken;
    }
  }
  return token;
};

/**
 * Store token when user logs in & initialize activity timestamp
 */
export const setAuthToken = (token) => {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(ACTIVITY_KEY, Date.now().toString());
  localStorage.removeItem(TOKEN_KEY); // ensure localStorage is clean
};

/**
 * Clear session from all browser storages
 */
export const clearAuthSession = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(ACTIVITY_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ACTIVITY_KEY);
};

/**
 * Check if the user has been inactive for more than 30 minutes
 */
export const isSessionExpired = () => {
  const token = getAuthToken();
  if (!token) return true;

  const lastActivity = sessionStorage.getItem(ACTIVITY_KEY);
  if (!lastActivity) return false; // First interaction

  const elapsed = Date.now() - parseInt(lastActivity, 10);
  return elapsed > INACTIVITY_LIMIT_MS;
};

/**
 * Refresh activity timestamp on user interaction
 */
export const updateActivityTimestamp = () => {
  if (getAuthToken()) {
    sessionStorage.setItem(ACTIVITY_KEY, Date.now().toString());
  }
};

/**
 * Force logout and redirect to login page
 */
export const logoutUser = (message) => {
  clearAuthSession();
  if (message) {
    alert(`🔒 ${message}`);
  }
  window.location.href = "/login";
};
